import { ReportAttributes, ReportRelationships } from 'dashboard/models/report';
import { ApiModel, ApiResource } from 'lib/interfaces/api';
import { Iso8601DateTimeString } from 'lib/types';
import { assertNever } from 'lib/utils/types';

export const ALL_PEP_CLASSES: PoliticallyExposedPersonClass[] = [
  'pep-class-1',
  'pep-class-2',
  'pep-class-3',
  'pep-class-4',
];

export type PoliticallyExposedPersonClass =
  | 'pep-class-1'
  | 'pep-class-2'
  | 'pep-class-3'
  | 'pep-class-4';

export interface PoliticallyExposedPersonBirthdate {
  granularity?: string;
  day?: string;
  month?: string;
  year?: string;
  'day-start'?: string;
  'month-start'?: string;
  'year-start'?: string;
  'day-end'?: string;
  'month-end'?: string;
  'year-end'?: string;
}

export interface PostingDataLink {
  dataset: string;
  'display-name': string;
  'default-url': string;
  'flag-code': string;
}

export interface PoliticallyExposedPersonPosition {
  'source-key'?: string;
  name: string;
  'pep-class': PoliticallyExposedPersonClass;
  'start-date': Iso8601DateTimeString;
  'end-date': Iso8601DateTimeString;
  'dataset-links'?: PostingDataLink[];
  topics: string[];
}

export interface PoliticallyExposedPersonCandidacy {
  'source-key'?: string;
  name: string;
  'pep-class': PoliticallyExposedPersonClass;
  'start-date': Iso8601DateTimeString;
  'end-date': Iso8601DateTimeString;
  'dataset-links'?: PostingDataLink[];
  topics: string[];
}

export interface PoliticallyExposedPersonAssociate {
  name: string;
  relationship: string;
  'pep-class': PoliticallyExposedPersonClass;
  topics: string[];
}

export interface PoliticallyExposedPersonImage {
  'external-url'?: string;
  'internal-url'?: string;
  'similarity-level'?: 'low' | 'medium' | 'high';
}

export interface PoliticallyExposedPersonInfoField {
  'source-key': string;
  name: string;
  url: string;
}

export interface PoliticallyExposedPersonSource {
  url: string;
  name: string;
  'flag-code'?: string;
}

export interface SourceMatchEntity {
  id?: string;
  names?: string[];
  aliases?: string[];
  birthdates?: PoliticallyExposedPersonBirthdate[];
  locations?: string[];
  sex?: string;
  positions?: string[];
  details?: string[];
}

export interface PoliticallyExposedPersonMatch {
  // TODO: Clean up this type. There is not parity between 2.0 and 1.0
  id?: string;
  'entity-id'?: string;
  aliases?: string[];
  name?: string;
  birthdates?: string[];
  'death-dates'?: string[];
  country?: string;
  locations?: string[];
  sex?: string;
  images: PoliticallyExposedPersonImage[];
  positions?: PoliticallyExposedPersonPosition[];
  candidacies?: PoliticallyExposedPersonCandidacy[];
  associates?: PoliticallyExposedPersonAssociate[];
  affiliations?: PoliticallyExposedPersonInfoField[];
  topics?: string[];
  sources?: Record<string, PoliticallyExposedPersonSource>;
  'related-urls'?: PoliticallyExposedPersonInfoField[];
  'match-types'?: string[];
  'pep-type'?: string;
  'data-source'?: string;
}

export interface PoliticallyExposedPersonIgnoreListEntry {
  'ignored-at': string;
  'run-history-completed-at': string;
  user: string;
  'ignore-reason': string;
  match: PoliticallyExposedPersonMatch;
}

export interface ReportPoliticallyExposedPersonAttributes extends ReportAttributes {
  query: {
    'name-first'?: string;
    'name-middle'?: string;
    'name-last'?: string;
    term?: string;
    birthdate?: string;
    'country-code'?: string;
  };
  result: PoliticallyExposedPersonMatch[];
  'enabled-classes'?: PoliticallyExposedPersonClass[];
  'ignore-list'?: PoliticallyExposedPersonIgnoreListEntry[];
  'profile-image'?: File;
  'profile-image-url'?: string;
}

export type ReportPoliticallyExposedPerson = ApiResource<
  ReportPoliticallyExposedPersonAttributes,
  ReportRelationships,
  ApiModel.ReportPoliticallyExposedPerson
>;

export type PoliticallyExposedPersonMatchType = 'primary_name' | 'aka';

export function pepClassDisplayText(pepClass: PoliticallyExposedPersonClass) {
  switch (pepClass) {
    case 'pep-class-1':
      return 'Class 1';
    case 'pep-class-2':
      return 'Class 2';
    case 'pep-class-3':
      return 'Class 3';
    case 'pep-class-4':
      return 'Class 4';
    default:
      assertNever(pepClass);
  }
}

export function translatePoliticallyExposedPersonMatchType(
  matchType?: PoliticallyExposedPersonMatchType,
) {
  if (!matchType) {
    return '';
  }

  switch (matchType) {
    case 'primary_name':
      return 'Primary Name';
    case 'aka':
      return 'AKA';
    default:
      assertNever(matchType);
  }
}
